import React from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarBlank, User as UserIcon } from '@phosphor-icons/react'
import { getTurkishLocativeSuffix } from '@/utils/abacus'
import type { AgendaWidgetData, UpcomingMeetingData } from '@/utils/abacus'

interface AgendaWidgetProps {
  agenda: AgendaWidgetData | null
  upcomingMeeting: UpcomingMeetingData | null
}

export function AgendaWidget({ agenda, upcomingMeeting }: AgendaWidgetProps) {
  const navigate = useNavigate()

  return (
    <section>
      <div className="flex items-center justify-between mb-4 px-1">
        <h3 className="text-[15px] font-bold uppercase tracking-[0.18em] text-espresso/80">
          Ajanda
        </h3>
        <span className="flex items-center gap-1.5 bg-espresso/[0.04] text-espresso/80 p-2 rounded-full border border-espresso/10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            overflow="visible"
            className="animated-iconoir"
            role="img"
            aria-label="calendar"
          >
            <path
              d="M15 4V2M15 4V6M15 4H10.5M3 10V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V10H3Z"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 10V6C3 4.89543 3.89543 4 5 4H7"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7 2V6"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M21 10V6C21 4.89543 20.1046 4 19 4H18.5"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>

      {/* Gold Background Container */}
      <div className="bg-gradient-to-br from-[#E6C96C] via-[#DCB645] to-[#B8922C] rounded-[24px] p-4 shadow-[0_12px_24px_rgba(212,175,55,0.2)] text-[#1A1A1A] relative overflow-hidden">
        <div className="space-y-3 relative z-10 w-full">
          {agenda ? (
            <div
              className="bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/30 rounded-[16px] p-3.5 flex flex-col gap-2.5 w-full cursor-pointer transition-all duration-300 relative group/agenda"
              onClick={() => navigate(`/groups/active?id=${agenda.groupId}`)}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 group-hover/agenda:opacity-100 group-hover/agenda:translate-x-full duration-1000 ease-in-out skew-x-12 transition-all rounded-[16px]"></div>
              
              {/* Top Row: Date & Group Info */}
              <div className="flex items-center gap-3.5">
                {/* Date Block */}
                <div className="bg-[#1A1A1A] text-white w-[56px] h-[56px] rounded-[14px] flex flex-col items-center justify-center shrink-0 shadow-lg relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#D4AF37] to-[#DCB645] opacity-60"></div>
                  <span className="text-[7.5px] leading-none uppercase font-extrabold tracking-[0.02em] text-[#D4AF37]/90 mt-1">
                    {agenda.formattedMonth}
                  </span>
                  <span className="text-[18px] font-black leading-none mt-0.5 text-white">
                    {agenda.formattedDay}
                  </span>
                  <span className="text-[8px] leading-none font-bold text-white/50 mt-0.5 mb-0.5">
                    {agenda.dayName}
                  </span>
                </div>

                {/* Group & Turn Info */}
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                   <h4 className="font-sans text-[18px] font-black tracking-tight leading-none text-[#1A1A1A] uppercase truncate mb-1.5">
                     {agenda.groupName}
                   </h4>
                  <div className="flex items-center gap-1.5 bg-white/40 backdrop-blur-sm self-start px-2 py-0.5 rounded-full border border-white/50 w-fit">
                    <UserIcon size={12} weight="bold" className="text-[#1A1A1A]/80 shrink-0" />
                    <span className="text-[11px] font-bold text-[#1A1A1A]/90 tracking-wide leading-none">
                      {agenda.isCurrentUserTurn
                        ? 'Sıra sende'
                        : `Sıra ${agenda.nextMemberName}${getTurkishLocativeSuffix(agenda.nextMemberName)}`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px w-full bg-[#1A1A1A]/10 my-0.5"></div>

              {/* Bottom Row: Date Text & Financial Info */}
              <div className="flex items-start justify-between gap-3 px-0.5">
                <div className="flex flex-col min-w-0 -mt-1 pt-0">
                   <span className="text-[9px] font-bold text-[#1A1A1A]/60 uppercase tracking-widest mb-1">TARİH</span>
                   <span className="flex items-center gap-1.5 text-[13px] font-bold text-[#1A1A1A] leading-none whitespace-nowrap">
                     <CalendarBlank size={13} weight="bold" className="text-[#1A1A1A]/70 shrink-0" />
                     {agenda.naturalDateText}
                   </span>
                </div>

                <div className="flex flex-col items-end shrink-0 text-right -mt-0.5">
                   <span className="text-[20px] font-black justify-end h-full flex items-center text-[#1A1A1A] leading-none whitespace-nowrap tracking-tight">
                     {agenda.financialText.main}
                   </span>
                   {agenda.financialText.sub && (
                     <span className="text-[10px] mt-1.5 font-bold bg-[#1A1A1A] text-[#DCB645] px-2 py-0.5 rounded-full leading-none whitespace-nowrap shadow-sm border border-[#1A1A1A]/5">
                       {agenda.financialText.sub}
                     </span>
                   )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white/20 backdrop-blur-md border border-white/40 rounded-[16px] p-5 flex flex-col items-center justify-center w-full min-h-[90px] gap-2 text-center">
              <CalendarBlank size={24} weight="duotone" className="text-[#1A1A1A]/40" />
              <p className="text-[13px] font-semibold text-[#1A1A1A]/60">
                Yaklaşan ödeme / alacak günü bulunmuyor
              </p>
            </div>
          )}

          {/* Card 2 - Buluşma Bilgisi (Ayrı bir günse veya ayrı bir grupsa render edilir) */}
          {upcomingMeeting && !upcomingMeeting.isSameAsPayment && (
            <div
              className="bg-white/30 backdrop-blur-sm border border-white/40 rounded-[18px] p-3 flex items-start gap-4 w-full cursor-pointer hover:bg-white/40 transition-colors"
              onClick={() => navigate(`/groups/active?id=${upcomingMeeting.groupId}`)}
            >
              <div className="bg-[#DCB645]/40 border border-[#B8922C]/30 w-[52px] h-[52px] rounded-[14px] flex flex-col items-center justify-center shrink-0 shadow-sm mt-0.5">
                <span className="text-[10px] leading-none uppercase font-bold tracking-[0.1em] text-espresso mt-0.5">
                  {upcomingMeeting.formattedMonth}
                </span>
                <span className="text-[20px] font-bold leading-none mt-1 text-espresso">
                  {upcomingMeeting.formattedDay}
                </span>
              </div>
              <div className="flex-1 mt-0.5 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <p className="text-[10.5px] font-bold text-[#1A1A1A]/80 uppercase tracking-wide">
                    Sıradaki Buluşma
                  </p>
                </div>
                <h4 className="font-sans text-[18px] font-extrabold tracking-tight leading-tight text-[#1A1A1A] uppercase truncate block">
                  {upcomingMeeting.groupName}
                </h4>
                <p className="text-[13px] mt-1 font-semibold text-[#1A1A1A]/90 leading-tight">
                  {upcomingMeeting.naturalDateText}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
